import { Logger } from "../logger";
import { Subject } from "rxjs";
import mime from "mime";
import { environment } from "app/environments/environment";
import { IUploadFile } from "../../domain/Upload/i-upload-file";
import { S3CloudProvider } from "./s3-cloud-provider";

export class S3UploadFile implements IUploadFile {
  constructor(
    private _id: string,
    private _file: File,
    private s3Filename: string,
    private notifyServer: (awsKey: string) => Promise<any>,
    private _customData: any,
    private evaporate,
    private bucketName: string,
    private logger: Logger,
    private _cdnServiceUrl: string,
    private _createdDate: Date,
    private regionEndpoint: string,
    private _s3Provider: S3CloudProvider,
    private tenantStorageOptionId?: string,
    private accessKey?: string,
  ) {}

  get id() {
    return this._id;
  }

  get file() {
    return this._file;
  }

  get customData() {
    return this._customData;
  }

  get createdDate() {
    return this._createdDate;
  }
  get progressChange() {
    return this._progressChange;
  }
  get progress() {
    return this._progress;
  }
  get isComplete() {
    return this._isComplete;
  }
  get isCompleteChange() {
    return this._isCompleteChange;
  }
  get isError() {
    return this._isError;
  }
  get isErrorChange() {
    return this._isErrorChange;
  }
  get isAborted() {
    return this._isAborted;
  }
  get abortedChange() {
    return this._abortedChange;
  }

  private started = false;
  private uploaded = false;
  private notifiedServer = false;
  private awsKey: string;
  private fileUrl: string;

  private config = {
    name: this.s3Filename,
    file: this.file,
    contentType: mime.getType(this.file.name),
    xAmzHeadersAtInitiate: {
      //'x-amz-acl': 'public-read'
      // 'x-amz-security-token': this.sessionToken
    },
    xAmzHeadersCommon: {
      // 'x-amz-security-token': this.sessionToken
    },
    notSignedHeadersAtInitiate: { "Content-Disposition": "attachment" },
    progress: (val) => {
      this.setProgress(val);
    },
  };

  private _progressChange = new Subject<number>();

  private _progress = 0;

  private _isComplete = false;

  private _isCompleteChange = new Subject<any>();

  private _isError = false;

  private _isErrorChange = new Subject<void>();

  private _isAborted = false;

  private _abortedChange = new Subject<void>();

  private getFileKey() {
    return `${this.bucketName}/${this.s3Filename}`;
  }

  private setProgress(val) {
    this._progress = val;
    this._progressChange.next(val);
  }

  private setComplete(notifyServerResult: any) {
    this._isComplete = true;
    this._isCompleteChange.next(notifyServerResult);
  }

  private setError(err) {
    this._isError = true;
    this._isErrorChange.next(err);
  }

  private setAbort() {
    this._isAborted = true;
    this._abortedChange.next();
  }

  async start() {
    if (this.started) {
      return;
    }

    this.started = true;

    const overrideParams = {
      bucket: this.bucketName,
      aws_url: this.regionEndpoint,
      signerUrl: `${environment.ENDPOINT}/api/s3/signv4/${
        this.tenantStorageOptionId ? this.tenantStorageOptionId : "default"
      }`,
    };
    if (this.accessKey) {
      overrideParams["aws_key"] = this.accessKey;
    }
    if (
      this._cdnServiceUrl &&
      this._cdnServiceUrl.length > 0 &&
      this.bucketName.trim().toLowerCase() ===
        this._s3Provider.protectedBucketName.trim().toLowerCase()
    ) {
      overrideParams.aws_url = this._cdnServiceUrl.replace(
        "{{bucketName}}",
        this.bucketName,
      );
      this.evaporate.config.cloudfront = true;
    }
    this.evaporate.config.maxConcurrentParts = 6;
    this.evaporate.config.s3Acceleration = true;

    this.evaporate
      .add(this.config, overrideParams)
      .then((awsKey) => {
        this.uploaded = true;
        this.awsKey = awsKey;
        this.fileUrl =
          this.regionEndpoint + "/" + this.bucketName + "/" + awsKey;
        //this.fileUrl = serviceUrlPlaceholder.replace('{{bucketName}}', this.bucketName)  + '/' + awsKey;
      })
      .then(() => {
        return this.notifyServer(this.fileUrl);
      })
      .then((notifyServerResult: any) => {
        this.notifiedServer = true;
        this.setComplete(notifyServerResult);
      })
      .catch((err) => {
        this.logger.warn(
          `error uploading file. uploaded to s3: ${this.uploaded}; Error: ${err}`,
        );
        this.setError(err ? err : "");
      });
  }

  async abort() {
    if (this.isComplete || !this.started || this.isAborted) {
      return;
    }

    if (!this.uploaded) {
      this.evaporate.cancel(this.getFileKey()).catch((err) => {
        this.logger.warn("Could not abort file upload to s3: " + err);
      });
    }

    this.setAbort();
  }

  async retry() {
    if (!this.isError || this.isAborted) {
      return;
    }

    this._isError = false;

    if (!this.uploaded) {
      this.started = false;
      this.setProgress(0);
      return this.start();
    } else if (!this.notifiedServer) {
      return this.notifyServer(this.awsKey)
        .then((notifyServerResult: any) => {
          this.notifiedServer = true;
          this.setComplete(notifyServerResult);
        })
        .catch((err) => {
          this.logger.warn(
            `error uploading file. uploaded to s3: ${this.uploaded}; Error: ${err}`,
          );
          this.setError(err ? err : "");
        });
    }
  }
}
